// Component: Segmented.jsx
// Purpose: DS Segmented control — a compact single-select facet filter (the
//          .segs/.seg pattern). Recessed group with a SLIDING thumb that glides
//          between options (measured via refs, animated by CSS on left/width at
//          --dur-fast). Options may carry an optional leading `icon`. Use for
//          VIEW-scoped filters in the Toolbar / page header.
//
// Usage:
//   <Segmented aria-label="View mode" value={mode} onChange={setMode}
//     options={[{ value: "card", label: "Cards", icon: <Icon/> }, …]} />

import React, { useLayoutEffect, useRef, useState } from "react";
import { cx } from "./cx";

export const Segmented = ({
  value,
  onChange,
  options = [],
  className,
  disabled = false,
  "aria-label": ariaLabel,
  ...props
}) => {
  const itemRefs = useRef({});
  const [thumb, setThumb] = useState({ left: 0, width: 0, ready: false });

  // Position the sliding thumb under the active option. useLayoutEffect runs
  // before paint, so the first frame already shows it in place (no mount slide);
  // later value changes animate via the CSS transition on left/width.
  useLayoutEffect(() => {
    const measure = () => {
      const el = itemRefs.current[value];
      if (el) setThumb({ left: el.offsetLeft, width: el.offsetWidth, ready: true });
    };
    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, [value, options]);

  return (
    <div className={cx("ds-segmented", className)} role="group" aria-label={ariaLabel} aria-disabled={disabled || undefined} {...props}>
      <span
        className="ds-segmented-thumb"
        aria-hidden="true"
        style={{ left: thumb.left, width: thumb.width, opacity: thumb.ready ? 1 : 0 }}
      />
      {options.map((opt) => {
        const active = opt.value === value;
        return (
          <button
            key={opt.value}
            ref={(el) => {
              itemRefs.current[opt.value] = el;
            }}
            type="button"
            className={cx("ds-segmented-item", active && "active")}
            aria-pressed={active}
            disabled={disabled}
            data-testid={opt.testId}
            onClick={() => !disabled && onChange?.(opt.value)}
          >
            {opt.icon && <span className="ds-seg-icon">{opt.icon}</span>}
            {opt.label}
          </button>
        );
      })}
    </div>
  );
};

export default Segmented;
